import TimetrackingApi from '../../common/api/zeiterfassungServerApi/TimetrackingApi';
import EmployeeApi from '../../common/api/zeiterfassungServerApi/EmployeeApi';
import { formatName } from '../../common/utils/employeeUtils';
import { EmployeeCalendarView } from './EmployeeCalendarView';
import { OffDaySummary, OffDayTypes } from './OffDaySummary';
import { TimetrackingToolbar } from './TimetrackingToolbar';
import useTranslate from '../../common/utils/translate';
import { getDays, useTimetrackingStore } from './utils';
import React, { useEffect, useMemo } from 'react';
import { Flex, Text } from '@chakra-ui/react';

type OffDay = {
  employee: number | null | undefined;
  type: 'HOLIDAY' | 'SICK' | 'PUBLIC_HOLIDAY' | 'OTHER';
};

const defaultOffDaysCount: OffDayTypes = {
  HOLIDAY: 0,
  SICK: 0,
  PUBLIC_HOLIDAY: 0,
  OTHER: 0,
};

export const TimeTracking: React.FC = () => {
  const t = useTranslate();
  const [{ data: employees = [], isFetched }] =
    EmployeeApi.hooks.useEmployeeQuery();
  const { currentDate, setOrderedEmployeeList } = useTimetrackingStore(
    ({ currentDate, setOrderedEmployeeList }) => ({
      currentDate,
      setOrderedEmployeeList,
    }),
  );

  useEffect(() => {
    if (isFetched) {
      setOrderedEmployeeList(employees);
    }
  }, [employees, isFetched]);

  const days = useMemo(() => getDays(currentDate), [currentDate]);

  const { data: yearlyOffDays = [] } = TimetrackingApi.hooks.useOffDaysOfYear<
    OffDay[]
  >(currentDate.getFullYear());

  const employeeMap = useMemo(
    () =>
      Object.fromEntries(
        employees?.map((emp) => [emp.id, formatName(emp)]) || [],
      ),
    [employees],
  );

  const employeeOffDaySummary = useMemo(() => {
    return yearlyOffDays.reduce<Record<string, OffDayTypes>>((acc, day) => {
      if (!day.employee) {
        employees.forEach((employee) => {
          const employeeId = employee.id.toString();
          acc[employeeId] = acc[employeeId] || { ...defaultOffDaysCount };
          acc[employeeId][day.type]++;
        });
        return acc;
      }
      const employeeId = day.employee.toString();
      acc[employeeId] = acc[employeeId] || { ...defaultOffDaysCount };
      acc[employeeId][day.type]++;
      return acc;
    }, {});
  }, [yearlyOffDays, employees]);

  if (!employees) {
    return (
      <Flex direction="column" gap={4} mt={4}>
        <TimetrackingToolbar />
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap={4} mt={10}>
      <TimetrackingToolbar />
      <EmployeeCalendarView employees={employees} days={days} />
      <Text pl={4}>{t('timeTracking.info.multipleSelection')}</Text>
      <OffDaySummary
        employeeOffDaySummary={employeeOffDaySummary}
        employeeMap={employeeMap}
      />
    </Flex>
  );
};
